import { useEffect, useState, useCallback } from "react";
import { useInvoicesStore } from "./invoicesStore";
import { InvoiceEditor } from "./InvoiceEditor";
import { Loader2, Check, AlertCircle } from "lucide-react";
import {
  Invoice,
  InvoiceStatusEnum,
  UpdateInvoiceParamsSchema,
  InvoiceLineInput,
} from "@forgepoint/shared";
import { useAutoSave } from "@/modules/quotesDeprecated/QuotePreview/hooks/useAutoSave";
import { cn } from "@/lib/utils";

interface InvoicePreviewControllerProps {
  invoiceId?: string;
  invoiceNumber?: number;
  showActionButtons?: boolean;
}

export function InvoicePreviewController({
  invoiceId,
  invoiceNumber,
  showActionButtons = true,
}: InvoicePreviewControllerProps) {
  const { getInvoice, updateInvoice, invoiceLoadingStates } =
    useInvoicesStore();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const onSave = useCallback(
    async (data: {
      salesOrderId?: string;
      customerCompanyId?: string;
      lines: InvoiceLineInput[];
    }) => {
      if (!invoice?.id) {
        return;
      }

      const updateData = {
        salesOrderId: data.salesOrderId,
        customerCompanyId: data.customerCompanyId,
        lines: data.lines,
      };

      await updateInvoice(invoice.id, updateData);
      setLastSaved(new Date());
      setValidationError(null);
    },
    [invoice?.id, updateInvoice],
  );

  const { autoSaving, queueEdit } = useAutoSave({
    onSave,
    timeout: 1000,
  });

  useEffect(() => {
    const loadInvoice = async () => {
      setLoading(true);
      setError(null);

      try {
        const fetchedInvoice = await getInvoice(invoiceId, invoiceNumber);
        if (fetchedInvoice) {
          setInvoice(fetchedInvoice);
        } else {
          setError("Invoice not found");
        }
      } catch (_err) {
        setError("Failed to load invoice");
      } finally {
        setLoading(false);
      }
    };

    if (invoiceId || invoiceNumber) {
      loadInvoice();
    }
  }, [invoiceId, invoiceNumber, getInvoice]);

  const handleChange = useCallback(
    (data: {
      salesOrderId?: string;
      customerCompanyId?: string;
      lines: InvoiceLineInput[];
    }) => {
      if (!invoice || invoice.status === InvoiceStatusEnum.FINALIZED) {
        return;
      }

      const result = UpdateInvoiceParamsSchema.safeParse(data);
      if (result.success) {
        setValidationError(null);
        queueEdit(data);
      } else {
        setValidationError(
          result.error.issues[0]?.message || "Invalid invoice data",
        );
      }
    },
    [invoice, queueEdit],
  );

  const loadingState = invoice?.id
    ? invoiceLoadingStates.get(invoice.id)
    : null;
  const isLoading = loading || loadingState?.isLoadingGet;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  const isFinalized = invoice?.status === InvoiceStatusEnum.FINALIZED;
  const canEdit = showActionButtons && !isFinalized;

  const getStatusMessage = () => {
    if (validationError) {
      return validationError;
    }
    if (autoSaving) {
      return "Saving...";
    }
    if (lastSaved) {
      const now = new Date();
      const diffSeconds = Math.floor(
        (now.getTime() - lastSaved.getTime()) / 1000,
      );
      if (diffSeconds < 60) {
        return "Saved";
      }
      return `Saved ${diffSeconds < 120 ? "1 minute ago" : `${Math.floor(diffSeconds / 60)} minutes ago`}`;
    }
    return null;
  };

  const statusMessage = getStatusMessage();

  return (
    <div className="flex flex-col h-full">
      {canEdit && statusMessage && (
        <div
          className={cn(
            "flex items-center gap-2 px-4 py-2 text-sm border-b",
            validationError
              ? "bg-red-50 text-red-700 border-red-200"
              : autoSaving
                ? "bg-blue-50 text-blue-700 border-blue-200"
                : "bg-green-50 text-green-700 border-green-200",
          )}
        >
          {validationError ? (
            <AlertCircle className="h-4 w-4" />
          ) : autoSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          <span>{statusMessage}</span>
        </div>
      )}
      <div className="flex-1 overflow-hidden">
        <InvoiceEditor
          invoice={invoice}
          readOnly={!canEdit}
          onChange={canEdit ? handleChange : undefined}
        />
      </div>
    </div>
  );
}
